<template>
    <div class="card">
        <div class="card-header p-0" id="headingFeatureInfo">
            <a
                class="btn btn-block text-left p-3"
                data-toggle="collapse"
                role="button"
                href="#collapseFeatureInfo"
                aria-expanded="false"
                aria-controls="collapseFeatureInfo">
                <h6 class="mb-0">{{ $t(`${modelTranslateID}.title`) }}</h6>
            </a>
        </div>

        <div id="collapseFeatureInfo"
            class="collapse"
            aria-labelledby="headingFeatureInfo"
            data-parent="">
            <div class="card-body">
                <div class="row">
                    <div class="col-12 col-sm-auto align-self-sm-center mb-3">
                        <div class="form-group">
                            <div class="form-check city-form-check pl-0">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :id="`cb-queryable`"
                                    :name="`queryable`"
                                    :disabled="readonlyForm || !this.hasResources"
                                    v-model="form.queryable"
                                    :false-value="false"
                                    :true-value="true"
                                    v-validate="''"
                                    :data-vv-scope="vScope">
                                <label class="form-check-label h-auto" :for="`cb-queryable`">
                                    <div class="d-flex flex-column align-items-start">
                                        <strong>{{ $t(`${modelTranslateID}.queryable.status`) }}</strong>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-sm-3">
                        <div class="form-group">
                            <MaterialSelect
                                :disabled="disableField"
                                class="city-form-group city-bg"
                                id="input-queryable-format"
                                name="queryable-format"
                                label="name"
                                :options="formats"
                                v-model.trim="form.format"
                                :reduce="option => option.code"
                                v-validate="{required: !!form.queryable}"
                                :data-vv-scope="vScope"
                                :inputClasses="[{'is-invalid': fnHasError('queryable-format', vScope) }]"
                                :error="fnFirstError('queryable-format', vScope)">
                                {{ $t(`${modelTranslateID}.queryable.format`) }}
                            </MaterialSelect>
                        </div>
                    </div>
                    <div class="col-12 col-sm-4 align-self-sm-center">
                        <div class="form-group mb-0">
                            <div class="form-check city-form-check pl-0">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :id="`cb-queryable-collapsible`"
                                    :name="`queryable-collapsible`"
                                    :disabled="disableField"
                                    v-model="form.collapsible"
                                    :false-value="false"
                                    :true-value="true"
                                    v-validate="''"
                                    :data-vv-scope="vScope"/>
                                <label class="form-check-label h-auto" :for="`cb-queryable-collapsible`">
                                    <div class="d-flex flex-column align-items-start">
                                        <strong>{{ $t(`${modelTranslateID}.queryable.collapsible`) }}</strong>
                                    </div>
                                </label>
                            </div>
                            <HelpSpan :helpText="`${helpTranslateID}.collapsible`" />
                        </div>
                    </div>
                    <div class="col-12 col-sm-3">
                        <div class="form-group">
                            <MaterialInput
                                :readonly="disableField"
                                class="city-form-group city-bg"
                                id="input-queryable-styles"
                                name="queryable-styles"
                                v-model.trim="form.styles"
                                v-validate="''"
                                :data-vv-scope="vScope"
                                :inputClasses="[{'is-invalid': fnHasError('queryable-styles', vScope) }]"
                                :error="fnFirstError('queryable-styles', vScope)"
                                :helpText="`${helpTranslateID}.styles`">
                                {{ $t(`${modelTranslateID}.queryable.styles`) }}
                            </MaterialInput>
                        </div>
                    </div>
                </div>

                <div class="row" v-if="showConfigFormatJson">
                    <div class="col-12">
                        <div class="row">
                            <div class="col">
                                <h6 class="font-weight-normal">{{ $t(`${modelTranslateID}.queryable.group`) }}</h6>
                                <hr class="mt-2" />
                            </div>
                        </div>
                    </div>
                    <div class="col-12">
                        <div class="row">
                            <div
                                class="col-sm"
                                v-for="(typename, idx) in (form.typenames ?? [])"
                                :key="`typename-${idx}`">
                                <LayerTypenameForm
                                    v-model="form.typenames[idx]"
                                    :idx="idx"
                                    :vScope="vScope"
                                    :parendId="idSubPanel"
                                    :readonlyForm="disableJsonField"
                                    :baseTranslateID="baseTranslateID"
                                    @onReset="onResetProperties(typename)"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ElementMixin from '../mixins/ElementMixin';
import TypenameMixin from '../mixins/TypenameMixin';


export default {
    name: 'LayerFeatureinfo',
    mixins: [
        ElementMixin,
        TypenameMixin,
    ],
    components: {
        LayerTypenameForm: () => import(/* webpackChunkName: "recursive-details-layer-typenames" */ './LayerTypenameForm.vue'),
        HelpSpan: () => import(/* webpackChunkName: "help-span" */ '@/components/form/HelpSpan.vue'),
    },
    props: {
        value: {
            type: Object,
            default: () => {
                return {};
            },
        },
        idSubPanel: {
            type: String,
            default: 'featureinfo',
        },
        formats: {
            type: Array,
            default: () => [],
        },
    },
    defaultObj: {
        queryable: false,
        format: null,
        collapsible: false,
        params: null,
        styles: null,
        typenames: [],
    },
    data() {
        return {
            form: this.$options.defaultObj,
        }
    },
    computed: {
        isActive() {
            return  this.form?.queryable ?? false;
        },
        isFormatJson() {
            return  this.form?.format && this.form?.format.toLowerCase() == 'json';
        },
        disableJsonField() {
            return this.disableField || !this.isFormatJson;
        },
        showConfigFormatJson() {
            return  this.isFormatJson && (this.form?.typenames ?? []).length>0;
        },
    },
    watch: {
        isFormatJson: {
            handler: function(value, oldValue){
                if(value && value != oldValue) {
                    this.setDescribeResourceProperties()
                }
            }
        },
    }
}
</script>